import { useQuery } from "@tanstack/react-query";

import { useGraphQLClient, useSubscription } from "@package/graphql/react";

import { graphql } from "../graphql";
import { useClaims, useUserId } from "../context/auth/hooks";

export const FeatureFlagsSubscription = graphql(`
  subscription FeatureFlagsSubscription($userId: String!, $orgId: String!) {
    feature_flags(
      where: {
        enabled: { _eq: true }
        _or: [
          { _and: [{ user_ids: { _is_null: true } }, { org_ids: { _is_null: true } }] }
          { user_ids: { _contains: [$userId] } }
          { org_ids: { _contains: [$orgId] } }
        ]
      }
    ) {
      id
      name
      metadata
    }
  }
`);

export const useFeatureFlags = () => {
  const userId = useUserId();
  const claims = useClaims();
  const orgId = claims.data?.["https://hasura.io/jwt/claims"]?.["x-hasura-org-id"] ?? "";
  return useSubscription(FeatureFlagsSubscription, {
    enabled: !!orgId,
    variables: { userId, orgId },
  });
};

/**
 * Every feature flag known to Hasura, regardless of `enabled` state or
 * user/org targeting (subject to row-level permissions). Used by the dev panel
 * to list all flags so they can be toggled locally. The live subscription above
 * only returns the flags that currently apply to the user.
 */
export const AllFeatureFlagsQuery = graphql(`
  query AllFeatureFlags {
    feature_flags {
      id
      name
      metadata
      enabled
    }
  }
`);

export const useAllFeatureFlags = () => {
  const graphql = useGraphQLClient();
  return useQuery({
    queryKey: ["all-feature-flags"],
    queryFn: async () => graphql.execute(AllFeatureFlagsQuery).then((res) => res.data?.feature_flags ?? []),
  });
};
